import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface FiscalConfig {
  id: string;
  restaurant_id: string;
  inscricao_estadual: string | null;
  regime_tributario: string;
  certificate_password: string | null;
  certificate_expires_at: string | null;
  certificate_uploaded: boolean;
  csc_id: string | null;
  csc_token: string | null;
  nfce_serie: number;
  nfce_numero_atual: number;
  ambiente: string;
  integrator: string | null;
  integrator_token: string | null;
  acbr_host: string | null;
  acbr_port: number | null;
  acbr_timeout_ms: number | null;
  created_at: string;
  updated_at: string;
}

export function useFiscalConfig() {
  const { activeRestaurant } = useRestaurantContext();
  const [config, setConfig] = useState<FiscalConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    if (!activeRestaurant?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("fiscal_configs")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .maybeSingle();

      if (error) throw error;
      setConfig(data);
    } catch (error) {
      console.error("Erro ao buscar configuração fiscal:", error);
    } finally {
      setLoading(false);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const createOrUpdateConfig = async (data: Partial<FiscalConfig>) => {
    if (!activeRestaurant?.id) return false;

    try {
      if (config) {
        // Update
        const { error } = await supabase
          .from("fiscal_configs")
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        // Create
        const { error } = await supabase
          .from("fiscal_configs")
          .insert({ ...data, restaurant_id: activeRestaurant.id });

        if (error) throw error;
      }

      toast.success("Configuração fiscal salva!");
      await fetchConfig();
      return true;
    } catch (error) {
      console.error("Erro ao salvar configuração fiscal:", error);
      toast.error("Erro ao salvar configuração");
      return false;
    }
  };

  return {
    config,
    loading,
    fetchConfig,
    createOrUpdateConfig,
  };
}
